import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Users, PlusCircle, Check, ImageUpscale, User } from 'lucide-react';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import { pages } from '../../components/utils/pages-links';
import { useAlert } from '../../context/alert/AlertProvider';
import { useTranslation } from 'react-i18next';
import TeamPreviewModal from '../../components/forms/collaboratorsForm/TeamPreviewModal';
import ExampleTeam from '../../components/forms/collaboratorsForm/ExampleTeam';
import TutorialOverlay from '../../components/ui/TutorialOverlay';
import NavigationButton from '../../components/ui/NavigationButton';
import ScenarioIndicator from '../../components/forms/scenario/ScenarioIndicator';
import CreateTeamModal from '../../components/profile/teamManagement/CreateTeamModal';
import { exampleTeam } from '../../components/utils/collaboratorsUtils';

const CollaboratorPage = () => {
  const [teams, setTeams] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();
  const { t } = useTranslation();
  const isExampleMode = location.state?.isExample;
  const [previewTeam, setPreviewTeam] = useState(null);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const exampleTeamRef = useRef(null);
  const nextButtonRef = useRef(null);
  const createTeamButtonRef = useRef(null);

  const tutorialSteps = [
    {
      title: t("tutorial.collaborators.title"),
      description: t("tutorial.collaborators.description"),
      position: "center"
    },
    {
      title: t("tutorial.collaborators.team.title"),
      description: t("tutorial.collaborators.team.description"),
      position: "top-right",
      elementRef: exampleTeamRef
    },
    {
      title: t("tutorial.collaborators.create.title"),
      description: t("tutorial.collaborators.create.description"),
      position: "center",
      elementRef: createTeamButtonRef
    },
    {
      title: t("tutorial.collaborators.next.title"),
      description: t("tutorial.general.next.description"),
      position: "next-button",
      elementRef: nextButtonRef
    }
  ];

  useEffect(() => {
    const cloneInfo = sessionStorage.getItem('cloneInfo');
    const draftInfo = sessionStorage.getItem('draftInfo');
    const savedSelectedTeamId = sessionStorage.getItem('selectedTeamId');

    const handleTeamsAndSelection = async () => {
      if (isExampleMode) {
        setShowTutorial(true);
        setIsLoading(false);
        return;
      }

      let fetchedTeams = [];
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`https://www.ia-schedule.com:4139/api/team`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          fetchedTeams = await response.json();

          fetchedTeams = fetchedTeams.map(team => ({
            ...team,
            source: null,
            matchedSource: null
          }));
        }
      } catch (error) {
        console.error('Error fetching teams:', error);
      }

      let additionalTeam = null;

      if (cloneInfo) {
        const clonedEmployees = JSON.parse(sessionStorage.getItem('employees') || '[]');
        if (clonedEmployees.length > 0) {
          const cloneInfoObj = JSON.parse(cloneInfo);
          additionalTeam = {
            id: 'cloned-team',
            name: cloneInfoObj.originalName || 'Cloned Team',
            employees: clonedEmployees,
            source: 'clone',
            matchedSource: null
          };
        }
      }
      else if (draftInfo) {
        const draftEmployees = JSON.parse(sessionStorage.getItem('employees') || '[]');
        if (draftEmployees.length > 0) {
          const draftInfoObj = JSON.parse(draftInfo);
          additionalTeam = {
            id: 'draft-team',
            name: draftInfoObj.name || 'Draft Team',
            employees: draftEmployees,
            source: 'draft',
            matchedSource: null
          };
        }
      }

      if (additionalTeam) {
        const additionalTeamEmployees = additionalTeam.employees.map(emp => emp.number).sort();
        const existingTeam = fetchedTeams.find(team => {
          const teamEmployees = team.employees.map(emp => emp.number).sort();

          if (teamEmployees.length !== additionalTeamEmployees.length) {
            return false;
          }

          for (let i = 0; i < teamEmployees.length; i++) {
            if (teamEmployees[i] !== additionalTeamEmployees[i]) {
              return false;
            }
          }

          return true;
        });

        if (existingTeam) {
          existingTeam.matchedSource = additionalTeam.source;
          setSelectedTeamId(existingTeam.id.toString());
          sessionStorage.setItem('selectedTeamId', existingTeam.id.toString());

          const formattedEmployees = existingTeam.employees.map(emp => ({
            name: emp.name,
            wkl_wrk_load: emp.wkl_Wrk_Load,
            day_WI: emp.day_WI,
            at_roster_num: emp.at_Roster_Num,
            number: emp.number,
            team: emp.team || 1
          }));
          sessionStorage.setItem('employees', JSON.stringify(formattedEmployees));
        } else {
          fetchedTeams = [...fetchedTeams, additionalTeam];
          setSelectedTeamId(additionalTeam.id.toString());
          sessionStorage.setItem('selectedTeamId', additionalTeam.id.toString());
        }
      }

      else if (savedSelectedTeamId) {
        setSelectedTeamId(savedSelectedTeamId);
      }

      setTeams(fetchedTeams);
      setIsLoading(false);
    };

    handleTeamsAndSelection();
  }, [isExampleMode]);

  const handleTeamSelect = (team) => {
    const formattedEmployees = team.employees.map(emp => ({
      name: emp.name,
      wkl_wrk_load: typeof emp.wkl_Wrk_Load !== 'undefined' ? emp.wkl_Wrk_Load : emp.wkl_wrk_load,
      day_WI: typeof emp.day_WI !== 'undefined' ? emp.day_WI : emp.day_wi,
      at_roster_num: typeof emp.at_Roster_Num !== 'undefined' ? emp.at_Roster_Num : 1,
      number: emp.number,
      team: emp.team || 1
    }));

    console.log('Formatted employees:', formattedEmployees);


    sessionStorage.setItem('employees', JSON.stringify(formattedEmployees));

    const savedEmployees = sessionStorage.getItem('employees');
    console.log('Saved employees (raw):', savedEmployees);
    console.log('Parsed saved employees:', JSON.parse(savedEmployees));

    const teamIdStr = team.id.toString();
    setSelectedTeamId(teamIdStr);
    sessionStorage.setItem('selectedTeamId', teamIdStr);
  };

  const handleCreateTeam = async (teamId, teamName, employees) => {
    try {
      const token = localStorage.getItem('token');

      const endpoint = 'https://www.ia-schedule.com:4139/api/team';

      const method = 'POST';

      const body = {
        name: teamName,
        employees: employees.map(emp => ({
          name: emp.name,
          wkl_wrk_load: emp.wkl_wrk_load * 10,
          day_WI: emp.day_WI * 10,
          at_roster_num: 1,
          number: emp.number
        }))
      };

      const response = await fetch(endpoint, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) throw new Error(t("alert.error.createTeam"));

      const newTeam = await response.json();
      setTeams([...teams, newTeam]);

      setShowCreateModal(false);
      showAlert({
        title: t("alert.success"),
        description: t("alert.success.createTeam"),
        variant: 'success'
      });
    } catch (error) {
      showAlert({
        title: t("alert.error"),
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const handleCreateNewTeam = () => {
    setShowCreateModal(true);
  };

  const handlePreviewTeam = (team) => {
    setPreviewTeam(team);
  };

  const handleNextTutorialStep = () => {
    if (tutorialStep < tutorialSteps.length - 1) {
      setTutorialStep(prev => prev + 1);
    } else {
      setShowTutorial(false);
    }
  };

  const handleNext = () => {

    if (isExampleMode) {
      sessionStorage.setItem('employees', JSON.stringify(exampleTeam.employees));
      sessionStorage.getItem('employees');
      sessionStorage.setItem('selectedTeamId', 'example-team');

      navigate('/form/general', {
        state: { isExample: isExampleMode }
      });
      return;
    }


    if (!sessionStorage.getItem('employees')) {
      showAlert({
        title: t('alert.error'),
        description: t('alert.error.selectTeam'),
        variant: 'destructive'
      });
      return;
    }

    navigate('/form/general', {
      state: { isExample: isExampleMode }
    });
  };

  return (
    <div className='-mt-16'>
      <Breadcrumbs pages={pages} />
      <div className='p-6 bg-gray-100 min-h-screen'>
        <div className='max-w-6xl mx-auto space-y-8'>
          <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0'>
            <h1 className='text-2xl font-bold'>
              {t("form.employee.title")}
            </h1>
            <div className='flex items-center gap-4 w-full sm:w-auto'>
              <NavigationButton ref={nextButtonRef} onClick={handleNext} className='ml-auto sm:ml-0' />
              <button
                ref={createTeamButtonRef}
                onClick={!isExampleMode ? handleCreateNewTeam : undefined}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${isExampleMode
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
                  }`}
                disabled={isExampleMode}
              >
                <PlusCircle className='w-5 h-5' />
                <span className='hidden sm:inline'>{t("form.employee.create")}</span>
              </button>
            </div>
          </div>

          <ScenarioIndicator />

          {isLoading ? (
            <div className='flex justify-center items-center h-64'>
              <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600'></div>
            </div>
          ) : isExampleMode ? (
            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6'>
              <ExampleTeam onPreview={handlePreviewTeam} />
            </div>
          ) : teams.length === 0 ? (
            <div className='bg-white shadow-lg rounded-lg p-6 sm:p-8 text-center'>
              <Users className='w-16 h-16 text-gray-400 mx-auto mb-6' />
              <h3 className='text-lg font-semibold text-gray-900 mb-2'>
                {t("form.employee.noTeams.title")}
              </h3>
              <p className='text-gray-600 mb-4'>
                {t("form.employee.noTeams.description")}
              </p>
              <button
                onClick={handleCreateNewTeam}
                className='px-4 sm:px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors'
              >
                {t("form.employee.noTeams.button")}
              </button>
            </div>
          ) : (
            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6'>
              {teams.map(team => (
                <div
                  key={team.id}
                  className={`bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 ease-in-out transform hover:-translate-y-1 relative
                  ${selectedTeamId === team.id.toString() ? 'border-2 border-indigo-600' : ''}
                  ${team.source ? 'ring-1 ring-indigo-200' : ''}`}
                >
                  {selectedTeamId === team.id.toString() && (
                    <div className='absolute top-2 right-2 bg-indigo-600 text-white rounded-full p-1'>
                      <Check className='w-4 h-4' />
                    </div>
                  )}
                  <div className='p-4 sm:p-6'>
                    <div className='flex items-center justify-between mb-4'>
                      <div className='flex items-center space-x-3 sm:space-x-4'>
                        <div className='bg-indigo-100 p-2 sm:p-3 rounded-full'>
                          <Users className='w-5 h-5 sm:w-6 sm:h-6 text-indigo-600' />
                        </div>
                        <div>
                          <h3 className='text-base sm:text-lg font-bold text-gray-900 break-words'>
                            {team.name}
                            <div className='flex flex-wrap gap-1 mt-1'>
                              {team.source === 'clone' && (
                                <span className='text-xs font-normal text-indigo-600 bg-indigo-50 px-2 py-1 rounded-full'>{t("form.employee.clone")}</span>
                              )}
                              {team.source === 'draft' && (
                                <span className='text-xs font-normal text-green-600 bg-green-50 px-2 py-1 rounded-full'>{t("form.employee.draft")}</span>
                              )}
                              {team.matchedSource === 'clone' && (
                                <span className='text-xs font-normal text-indigo-600 bg-indigo-50 px-2 py-1 rounded-full'>{t("form.employee.usedClone")}</span>
                              )}
                              {team.matchedSource === 'draft' && (
                                <span className='text-xs font-normal text-green-600 bg-green-50 px-2 py-1 rounded-full'>{t("form.employee.usedDraft")}</span>
                              )}
                            </div>
                          </h3>
                          <p className='text-xs sm:text-sm text-gray-500'>
                            {team.employees.length} {t('teams.count')}{team.employees.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className='flex justify-between items-center mt-3 sm:mt-4 pt-3 sm:pt-4 border-t'>
                      <button
                        onClick={() => handlePreviewTeam(team)}
                        className='px-3 sm:px-4 py-2 text-indigo-600 border border-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors text-xs sm:text-sm'
                      >
                        {t("form.employee.preview")}
                      </button>
                      <button
                        onClick={() => handleTeamSelect(team)}
                        className='px-3 sm:px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-xs sm:text-sm'
                      >
                        {t("form.employee.select")}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {previewTeam && (
        <TeamPreviewModal
          team={previewTeam}
          onClose={() => setPreviewTeam(null)}
        />
      )}

      {showCreateModal && (
        <CreateTeamModal
          onClose={() => {
            setShowCreateModal(false);
          }}
          onCreate={handleCreateTeam}
        />
      )}

      <TutorialOverlay
        isVisible={showTutorial}
        onClose={() => setShowTutorial(false)}
        title={tutorialSteps[tutorialStep]?.title}
        description={tutorialSteps[tutorialStep]?.description}
        position={tutorialSteps[tutorialStep]?.position}
        targetRef={tutorialSteps[tutorialStep]?.elementRef}
        onNext={handleNextTutorialStep}
        isLastStep={tutorialStep === tutorialSteps.length - 1}
      />

    </div>
  );
};

export default CollaboratorPage;